import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  buildPublicAvailabilityQuery,
  filterFutureSlots,
  formatSlotDateTime,
  getBusinessDateString,
  resetStartTimeSelection,
  selectSlotStartTime,
} from "../lib/publicBookingSlots";
import { backendUrl, BACKEND_UNREACHABLE_MESSAGE } from "../lib/backendUrl";
import { AvailabilitySelector } from "../components/AvailabilitySelector";

const INITIAL_FORM = {
  service_id: "",
  staff_id: "",
  date: "",
  start_time: "",
  name: "",
  email: "",
  phone: "",
};

const toneClasses = {
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  error: "border-red-200 bg-red-50 text-red-700",
};

const CONFLICT_RECOVERY_MESSAGE = "Questo orario è appena stato occupato. Scegline uno nuovo.";
const PAST_SLOT_MESSAGE = "Questo orario non è più disponibile. Scegli un orario futuro.";
const EMPTY_FEEDBACK = {
  tone: "neutral",
  title: "",
  message: "",
};

const STEP_GUIDANCE = {
  1: "Scegli il servizio che vuoi prenotare. Se vuoi, indica una preferenza per l’operatore.",
  2: "Scegli se indicare giorno e orario o cercare il primo appuntamento disponibile.",
  3: "Inserisci i tuoi dati per la prenotazione.",
  4: "Controlla i dettagli e conferma.",
};

function formatValue(value, fallback = "Non disponibile") {
  if (value === undefined || value === null || String(value).trim() === "") {
    return fallback;
  }
  return String(value);
}

function formatPrice(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "";
  }
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(numericValue);
}

function extractErrorMessage(payload, fallbackMessage) {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof detail?.message === "string" && detail.message.trim()) {
    return detail.message.trim();
  }
  return fallbackMessage;
}

function formatAppointmentStatus(status) {
  if (status === "scheduled") {
    return "Prenotato";
  }
  if (status === "requested") {
    return "Richiesto";
  }
  return formatValue(status);
}

export function PublicBookingPage() {
  const stepTitleRef = useRef(null);
  const feedbackRef = useRef(null);
  const successTitleRef = useRef(null);
  const conflictRecoveryPendingRef = useRef(false);
  const { businessIdOrSlug } = useParams();
  const [businessInfo, setBusinessInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
  }));
  const [pageState, setPageState] = useState("loading");
  const [pageRefreshKey, setPageRefreshKey] = useState(0);
  const [availabilityState, setAvailabilityState] = useState("idle");
  const [availabilityRefreshKey, setAvailabilityRefreshKey] = useState(0);
  const [schedulingMode, setSchedulingMode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(EMPTY_FEEDBACK);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    stepTitleRef.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (step !== 2 || !conflictRecoveryPendingRef.current) {
      return;
    }

    conflictRecoveryPendingRef.current = false;
    window.scrollTo({ top: 0, behavior: "smooth" });
    feedbackRef.current?.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (!result?.appointment) {
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    successTitleRef.current?.focus({ preventScroll: true });
  }, [result]);

  useEffect(() => {
    let active = true;

    const loadPublicBookingContext = async () => {
      if (!businessIdOrSlug) {
        setPageState("invalid");
        return;
      }

      setPageState("loading");
      try {
        const [infoResponse, servicesResponse, staffResponse] = await Promise.all([
          fetch(`${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/info`),
          fetch(`${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/services`),
          fetch(`${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/staff`),
        ]);

        const [infoPayload, servicesPayload, staffPayload] = await Promise.all([
          infoResponse.json().catch(() => null),
          servicesResponse.json().catch(() => []),
          staffResponse.json().catch(() => []),
        ]);

        if (!active) {
          return;
        }

        if (!infoResponse.ok || !infoPayload?.business_id) {
          setPageState("invalid");
          return;
        }

        setBusinessInfo(infoPayload);
        const businessDate = infoPayload.current_date || getBusinessDateString(
          new Date(),
          infoPayload.timezone || "Europe/Rome"
        );
        setForm((currentForm) => ({
          ...currentForm,
          date: currentForm.date && currentForm.date >= businessDate
            ? currentForm.date
            : businessDate,
        }));
        setServices(Array.isArray(servicesPayload) ? servicesPayload : []);
        setStaff(Array.isArray(staffPayload) ? staffPayload : []);
        setPageState("ready");
      } catch (error) {
        if (active) {
          setPageState("error");
        }
      }
    };

    loadPublicBookingContext();
    return () => {
      active = false;
    };
  }, [businessIdOrSlug, pageRefreshKey]);

  useEffect(() => {
    let active = true;

    const loadAvailability = async () => {
      if (schedulingMode !== "manual" || !businessIdOrSlug || !form.service_id || !form.date) {
        setSlots([]);
        setAvailabilityState("idle");
        return;
      }

      setAvailabilityState("loading");
      setFeedback((currentFeedback) => (
        currentFeedback.title === "Disponibilità non caricata"
          ? EMPTY_FEEDBACK
          : currentFeedback
      ));
      try {
        const params = buildPublicAvailabilityQuery({
          serviceId: form.service_id,
          staffId: form.staff_id,
          date: form.date,
        });
        const response = await fetch(
          `${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/availability?${params}`
        );
        const payload = await response.json().catch(() => null);

        if (!active) {
          return;
        }

        if (!response.ok) {
          setSlots([]);
          setAvailabilityState("error");
          setFeedback({
            tone: "warning",
            title: "Disponibilità non caricata",
            message: extractErrorMessage(payload, "Non siamo riusciti a caricare gli orari disponibili."),
          });
          return;
        }

        setSlots(filterFutureSlots(payload?.available_slots, payload?.current_time || new Date()));
        setAvailabilityState("ready");
      } catch (error) {
        if (active) {
          setSlots([]);
          setAvailabilityState("error");
          setFeedback({
            tone: "warning",
            title: "Disponibilità non caricata",
            message: BACKEND_UNREACHABLE_MESSAGE,
          });
        }
      }
    };

    loadAvailability();
    return () => {
      active = false;
    };
  }, [businessIdOrSlug, form.service_id, form.staff_id, form.date, schedulingMode, availabilityRefreshKey]);

  const selectedService = services.find((item) => item.id === form.service_id) || null;
  const selectedStaff = staff.find((item) => item.id === form.staff_id) || null;
  const eligibleStaff = selectedService
    ? staff.filter((member) => {
      if (!selectedService.service_type || !Array.isArray(member.specialties)) return true;
      const serviceType = selectedService.service_type.trim().toLocaleLowerCase("it-IT");
      return member.specialties.some(
        (specialty) => String(specialty).trim().toLocaleLowerCase("it-IT") === serviceType
      );
    })
    : [];
  const businessTimezone = businessInfo?.timezone || "Europe/Rome";
  const businessToday = businessInfo?.current_date || getBusinessDateString(new Date(), businessTimezone);
  const statusToneClass = toneClasses[feedback.tone] || toneClasses.neutral;
  const isAlertFeedback = feedback.tone === "warning" || feedback.tone === "error";

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "service_id" || name === "staff_id") {
      setSchedulingMode("");
      setSlots([]);
      setAvailabilityState("idle");
    }
    setForm((currentForm) => {
      const nextForm = {
        ...currentForm,
        [name]: value,
      };
      if (name === "service_id") {
        nextForm.staff_id = "";
      }
      if (name === "service_id" || name === "staff_id" || name === "date") {
        return resetStartTimeSelection(nextForm);
      }
      return nextForm;
    });
    if (result) {
      setResult(null);
    }
  };

  const handleSlotSelection = (slot) => {
    setForm((currentForm) => selectSlotStartTime(currentForm, slot));
    setResult(null);
  };

  const retryPublicBookingContext = () => {
    setPageState("loading");
    setPageRefreshKey((currentKey) => currentKey + 1);
  };

  const retryAvailability = () => {
    setFeedback(EMPTY_FEEDBACK);
    setAvailabilityState("loading");
    setAvailabilityRefreshKey((currentKey) => currentKey + 1);
  };

  const findFirstAvailability = async () => {
    const params = new URLSearchParams({
      service_id: form.service_id,
      start_date: businessToday,
      slot_step_minutes: "5",
    });
    if (form.staff_id) {
      params.set("staff_id", form.staff_id);
    }
    const response = await fetch(
      `${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/availability/first?${params}`
    );
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const error = new Error("First availability failed");
      error.userMessage = extractErrorMessage(payload, "Non siamo riusciti a cercare la prima disponibilità.");
      throw error;
    }
    return payload;
  };

  const handleSchedulingModeChange = (nextMode) => {
    setSchedulingMode(nextMode);
    setForm((currentForm) => resetStartTimeSelection({
      ...currentForm,
      date: businessToday,
    }));
    setSlots([]);
    setAvailabilityState("idle");
    setResult(null);
  };

  const submitBooking = async (event) => {
    event.preventDefault();
    if (submitting || !form.start_time) {
      return;
    }

    setSubmitting(true);
    setFeedback({
      tone: "neutral",
      title: "Invio prenotazione",
      message: "Stiamo registrando la tua richiesta.",
    });

    try {
      const response = await fetch(
        `${backendUrl}/api/public/bookings/${encodeURIComponent(businessIdOrSlug)}/appointments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            service_id: form.service_id,
            ...(form.staff_id ? { staff_id: form.staff_id } : {}),
            start_time: form.start_time,
            name: form.name,
            email: form.email || null,
            phone: form.phone || null,
          }),
        }
      );
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        if (response.status === 409) {
          conflictRecoveryPendingRef.current = true;
          setFeedback({
            tone: "warning",
            title: "Slot non più disponibile",
            message: CONFLICT_RECOVERY_MESSAGE,
          });
          setForm((currentForm) => resetStartTimeSelection(currentForm));
          setSlots([]);
          setAvailabilityState(schedulingMode === "manual" ? "loading" : "idle");
          if (schedulingMode !== "manual") setSchedulingMode("");
          setStep(2);
          if (schedulingMode === "manual") {
            setAvailabilityRefreshKey((currentKey) => currentKey + 1);
          }
          return;
        }

        const errorMessage = extractErrorMessage(payload, "Non siamo riusciti a registrare la prenotazione.");
        if (errorMessage === PAST_SLOT_MESSAGE) {
          setFeedback({
            tone: "warning",
            title: "Orario non più disponibile",
            message: PAST_SLOT_MESSAGE,
          });
          setForm((currentForm) => resetStartTimeSelection(currentForm));
          setSlots([]);
          setAvailabilityState(schedulingMode === "manual" ? "loading" : "idle");
          if (schedulingMode !== "manual") setSchedulingMode("");
          setStep(2);
          if (schedulingMode === "manual") {
            setAvailabilityRefreshKey((currentKey) => currentKey + 1);
          }
          return;
        }

        setFeedback({
          tone: "error",
          title: "Prenotazione non riuscita",
          message: errorMessage,
        });
        return;
      }

      setResult(payload);
      setFeedback({
        tone: "success",
        title: "Prenotazione registrata",
        message: payload?.status_message || "Abbiamo registrato correttamente il tuo appuntamento.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        title: "Errore temporaneo",
        message: BACKEND_UNREACHABLE_MESSAGE,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const startNewBooking = () => {
    setResult(null);
    setFeedback(EMPTY_FEEDBACK);
    setForm((currentForm) => ({
      ...INITIAL_FORM,
      date: currentForm.date,
    }));
    setSlots([]);
    setAvailabilityState("idle");
    setSchedulingMode("");
    setStep(1);
  };

  const isConflictRecoveryFeedback = feedback.message === CONFLICT_RECOVERY_MESSAGE;
  const availabilityLiveMessage = step === 2 && schedulingMode === "manual" && !isConflictRecoveryFeedback
    ? (
      availabilityState === "loading"
        ? "Stiamo cercando gli orari disponibili."
        : availabilityState === "ready" && slots.length === 0
          ? "Nessun orario disponibile per questo giorno."
          : ""
    )
    : "";

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="min-h-screen bg-slate-50 px-4 py-8 outline-none"
      data-testid="public-booking-page"
    >
      <section className="mx-auto w-full max-w-3xl space-y-4">
        <div className="rounded-sm border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">FullBook Public Booking</p>

          {pageState === "loading" && (
            <div role="status" aria-live="polite" data-testid="public-booking-initial-loading">
              <h1 className="mt-4 text-2xl font-semibold text-slate-900 section-title">Prenotazione online</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Stiamo caricando le informazioni necessarie per prenotare.
              </p>
            </div>
          )}

          {pageState === "invalid" && (
            <div role="alert">
              <h1 className="mt-4 text-2xl font-semibold text-slate-900 section-title">Pagina non disponibile</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Questo link di prenotazione non è valido o non è più disponibile.
              </p>
            </div>
          )}

          {pageState === "error" && (
            <div role="alert" data-testid="public-booking-initial-error">
              <h1 className="mt-4 text-2xl font-semibold text-slate-900 section-title">Errore temporaneo</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {BACKEND_UNREACHABLE_MESSAGE}
              </p>
              <button
                type="button"
                onClick={retryPublicBookingContext}
                className="mt-4 h-11 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white"
                data-testid="public-booking-initial-retry"
              >
                Riprova
              </button>
            </div>
          )}

          {pageState === "ready" && businessInfo && !result?.appointment && (
            <>
              <h1 className="mt-4 text-2xl font-semibold text-slate-900 section-title">
                Prenota da {formatValue(businessInfo.business_label, "questa attività")}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                Completa i passaggi per inviare la tua richiesta di prenotazione.
              </p>
              {!businessInfo.booking_enabled && (
                <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                  {formatValue(businessInfo.disabled_reason, "Prenotazioni online non disponibili in questo momento.")}
                </div>
              )}
            </>
          )}
        </div>

        {pageState === "ready" && businessInfo?.booking_enabled && (
          <>
            {!result?.appointment && (
              <>
                <div
                  ref={feedbackRef}
                  className={`rounded-sm border p-4 text-sm outline-none ${statusToneClass}`}
                  data-testid="public-booking-feedback"
                  role={isAlertFeedback ? "alert" : feedback.message ? "status" : undefined}
                  aria-live={isAlertFeedback ? undefined : feedback.message ? "polite" : undefined}
                  tabIndex={isConflictRecoveryFeedback ? -1 : undefined}
                >
                  <p className="font-medium">{feedback.title || "Prenotazione online"}</p>
                  <p className="mt-1" data-testid="public-booking-step-guidance">
                    {feedback.message || STEP_GUIDANCE[step]}
                  </p>
                  {step === 2 && availabilityState === "error" && (
                    <button
                      type="button"
                      onClick={retryAvailability}
                      className="mt-3 min-h-11 rounded-sm border border-current bg-white px-3 py-2 text-sm font-medium"
                      data-testid="public-booking-availability-retry"
                    >
                      Riprova disponibilità
                    </button>
                  )}
                </div>

                <form
                  className="rounded-sm border border-slate-200 bg-white p-6 shadow-sm"
                  onSubmit={submitBooking}
                  data-testid="public-booking-form"
                >
              <h2 ref={stepTitleRef} tabIndex={-1} className="text-lg font-semibold section-title outline-none">
                {step === 1 && "Servizio e operatore"}
                {step === 2 && "Giorno, fascia e orario"}
                {step === 3 && "Dati cliente"}
                {step === 4 && "Riepilogo e conferma"}
              </h2>
              <p className="mt-1 text-xs text-slate-500">Step {step} di 4</p>

              {step === 1 && (
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Servizio</span>
                      <select name="service_id" value={form.service_id} onChange={handleChange} className="h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm" data-testid="public-booking-service" required>
                        <option value="">Seleziona servizio</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} {service.duration_minutes ? `· ${service.duration_minutes} min` : ""}{" "}
                            {service.price !== undefined ? `· ${formatPrice(service.price)}` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Operatore <span className="font-normal text-slate-500">· Facoltativo</span></span>
                      <select name="staff_id" value={form.staff_id} onChange={handleChange} disabled={!form.service_id} className="h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50" data-testid="public-booking-staff">
                        <option value="">Nessuna preferenza</option>
                        {eligibleStaff.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                      </select>
                      <span className="block text-xs text-slate-500">Senza preferenza assegneremo un operatore disponibile.</span>
                    </label>
                  </div>
                  <button type="button" disabled={!form.service_id} onClick={() => setStep(2)} className="h-11 w-full rounded-sm bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-60" data-testid="public-booking-step-1-continue">Continua</button>
                </div>
              )}

              {step === 2 && (
                <div className="mt-6 space-y-4">
                  <p className="text-xs text-slate-500">
                    {selectedService?.name} · {selectedStaff?.name || "operatore assegnato automaticamente"}
                  </p>
                  {availabilityLiveMessage && (
                    <p
                      className="sr-only"
                      role="status"
                      aria-live="polite"
                      data-testid="public-booking-availability-status"
                    >
                      {availabilityLiveMessage}
                    </p>
                  )}
                  <AvailabilitySelector
                    date={form.date}
                    onDateChange={(value) => {
                      setForm((currentForm) => resetStartTimeSelection({ ...currentForm, date: value }));
                      setResult(null);
                    }}
                    slots={slots}
                    loading={availabilityState === "loading"}
                    error={availabilityState === "error" ? "Disponibilità non caricata." : ""}
                    selectedStartTime={form.start_time}
                    onSelectSlot={handleSlotSelection}
                    onFindFirstAvailability={findFirstAvailability}
                    schedulingMode={schedulingMode}
                    onSchedulingModeChange={handleSchedulingModeChange}
                    slotStepMinutes={5}
                    minDate={businessToday}
                    timeZone={businessTimezone}
                    testIdPrefix="public-booking"
                    dateLabel="Scegli il giorno"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setStep(1)} className="h-11 rounded-sm border border-slate-200 bg-white text-sm font-medium">Indietro</button>
                    <button type="button" disabled={!form.start_time} onClick={() => setStep(3)} className="h-11 rounded-sm bg-slate-900 text-sm font-medium text-white disabled:opacity-60" data-testid="public-booking-step-2-continue">Continua</button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="mt-6 space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 text-sm md:col-span-2">
                      <span className="font-medium text-slate-700">Nome</span>
                      <input name="name" value={form.name} onChange={handleChange} placeholder="Come ti chiami?" className="h-11 w-full rounded-sm border border-slate-200 px-3 text-sm" data-testid="public-booking-name" required />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Email</span>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="nome@email.it" className="h-11 w-full rounded-sm border border-slate-200 px-3 text-sm" data-testid="public-booking-email" />
                    </label>
                    <label className="space-y-2 text-sm">
                      <span className="font-medium text-slate-700">Telefono</span>
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="Telefono" className="h-11 w-full rounded-sm border border-slate-200 px-3 text-sm" data-testid="public-booking-phone" />
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">Inserisci almeno email o telefono per permettere al negozio di ritrovare la prenotazione.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setStep(2)} className="h-11 rounded-sm border border-slate-200 bg-white text-sm font-medium">Indietro</button>
                    <button type="button" disabled={!form.name || (!form.email && !form.phone)} onClick={() => setStep(4)} className="h-11 rounded-sm bg-slate-900 text-sm font-medium text-white disabled:opacity-60" data-testid="public-booking-step-3-continue">Riepilogo</button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="mt-6 space-y-4" data-testid="public-booking-summary">
                  <dl className="space-y-2 rounded-sm border border-slate-200 bg-slate-50 p-4 text-sm">
                    <div><dt className="text-slate-500">Servizio</dt><dd className="font-medium text-slate-900">{selectedService?.name}</dd></div>
                    <div><dt className="text-slate-500">Operatore</dt><dd className="font-medium text-slate-900">{selectedStaff?.name || "Assegnazione automatica"}</dd></div>
                    <div><dt className="text-slate-500">Quando</dt><dd className="font-medium text-slate-900">{formatSlotDateTime(form.start_time, businessTimezone)}</dd></div>
                    <div><dt className="text-slate-500">Cliente</dt><dd className="font-medium text-slate-900">{form.name}</dd></div>
                  </dl>
                  <aside
                    className="rounded-sm border border-slate-200 bg-white p-4 text-xs leading-relaxed text-slate-600"
                    data-testid="public-booking-privacy-notice"
                    aria-label="Informazioni sul trattamento dei dati"
                  >
                    I dati inseriti (nome, email e/o telefono), il servizio, l’eventuale preferenza per
                    l’operatore e i dati dell’appuntamento saranno usati e conservati nello storico per
                    gestire la prenotazione. I dati sono trattati dall’attività che riceve la prenotazione;
                    FullBook fornisce il servizio di prenotazione. Per richieste sui tuoi dati, contatta
                    direttamente l’attività.
                  </aside>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setStep(3)} className="h-11 rounded-sm border border-slate-200 bg-white text-sm font-medium">Indietro</button>
                    <button type="submit" disabled={submitting || !form.start_time} className="h-11 rounded-sm bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-60" data-testid="public-booking-submit">
                      {submitting ? "Invio in corso..." : "Conferma prenotazione"}
                    </button>
                  </div>
                </div>
              )}
                </form>
              </>
            )}

            {result?.appointment && (
              <div
                className="rounded-sm border border-emerald-200 bg-white p-6 shadow-sm outline-none"
                data-testid="public-booking-result"
                role="status"
              >
                <p className="text-xs uppercase tracking-[0.18em] text-emerald-600">Prenotazione confermata</p>
                <h1
                  ref={successTitleRef}
                  tabIndex={-1}
                  className="mt-3 text-2xl font-semibold text-slate-900 section-title outline-none"
                  data-testid="public-booking-success-title"
                >
                  Il tuo appuntamento è stato registrato
                </h1>
                <p className="mt-3 text-sm text-emerald-700">
                  {feedback.message || "Abbiamo registrato correttamente il tuo appuntamento."}
                </p>
                <div className="mt-4 grid gap-4 text-sm md:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Servizio</p>
                    <p className="mt-1 text-slate-900">{formatValue(result.appointment.service_name)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Operatore</p>
                    <p className="mt-1 text-slate-900">{formatValue(result.appointment.staff_name)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Inizio</p>
                    <p className="mt-1 text-slate-900">{formatSlotDateTime(result.appointment.start_time, businessTimezone)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Stato</p>
                    <p className="mt-1 text-slate-900">{formatAppointmentStatus(result.appointment.status)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={startNewBooking}
                  className="mt-6 h-11 w-full rounded-sm bg-slate-900 px-4 text-sm font-medium text-white"
                  data-testid="public-booking-new-booking"
                >
                  Effettua un’altra prenotazione
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
